import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "./Header";
import Footer from "./Footer";
import { Clock, IndianRupee, CheckCircle } from "lucide-react";
import axios from "axios";
import "./Careers.css";

// Opening positions
const openPositions = [
  {
    id: "mern-stack",
    title: "MERN Stack Developer",
    description:
      "We're looking for a skilled developer to build scalable and high-performance web applications using the MERN stack.",
    type: "Full-time",
    compensation: "Not Disclosed",
    skills: ["React", "Node.js", "MongoDB", "Express"],
  },
  {
    id: "ui-ux",
    title: "UI/UX Developer",
    description:
      "We seek a creative professional to design and implement exceptional user interfaces and experiences.",
    type: "Full-time",
    compensation: "Not Disclosed",
    skills: ["Figma", "Sketch", "HTML/CSS", "JavaScript"],
  },
  {
    id: "mobile-developer",
    title: "Mobile Developer",
    description: "We're looking for a Senior Mobile Developer to join our team.",
    type: "Full-time",
    compensation: "Not Disclosed",
    skills: ["React Native", "iOS", "Android", "Flutter"],
  },
];

// Initial form data
const initialFormData = {
  name: "",
  email: "",
  mobile: "",
  location: "",
  position: "",
  message: "",
  resumeLink: "",
};

const Careers = () => {
  const applicationFormRef = useRef(null);
  const imageColumnRef = useRef(null);

  const [selectedPosition, setSelectedPosition] = useState("Select Position");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [imageAnimated, setImageAnimated] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  // CRA BACKEND URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Trigger animation on scroll
  useEffect(() => {
    const currentFormRef = applicationFormRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !imageAnimated) {
          setImageAnimated(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (currentFormRef) {
      observer.observe(currentFormRef);
    }

    return () => {
      if (currentFormRef) observer.unobserve(currentFormRef);
    };
  }, [imageAnimated, applicationFormRef, setImageAnimated]);

  const jobTitles = ["Select Position", ...openPositions.map((p) => p.title)];

  const handleApplyClick = (positionTitle) => {
    setSelectedPosition(positionTitle);
    setFormData((prev) => ({ ...prev, position: positionTitle }));

    if (applicationFormRef.current) {
      const currentFormRef = applicationFormRef.current;
      setTimeout(() => {
        currentFormRef.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "position") {
      setSelectedPosition(value);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // SUBMIT TO RENDER BACKEND
  const handleSubmit = (e) => {
    e.preventDefault();

    axios
      .post(`${API_BASE_URL}/api/careers/apply`, formData)
      .then((response) => {
        console.log("SUCCESS!", response.data);
        setFormData(initialFormData);
        setSelectedPosition("Select Position");
        setIsSubmitted(true);
      })
      .catch((error) => {
        console.error("FAILED...", error);
        alert("Application failed to send. Please try again.");
      });
  };

  const closeSuccessModal = () => {
    setIsSubmitted(false);
    window.scrollTo(0, 0);
  };

  const renderJobCard = (job) => (
    <div key={job.id} className="job-card card">
      <h3>{job.title}</h3>
      <p className="job-description">{job.description}</p>
      <div className="job-meta">
        <span>
          <Clock size={16} /> {job.type}
        </span>
        <span>
          <IndianRupee size={16} /> {job.compensation}
        </span>
      </div>
      <div className="job-skills">
        {job.skills.map((skill) => (
          <span key={skill} className="skill-tag">
            {skill}
          </span>
        ))}
      </div>
      <button className="button-small" onClick={() => handleApplyClick(job.title)}>
        Apply Now
      </button>
    </div>
  );

  return (
    <div className="careers-page">
      <Helmet>
        <title>Careers | VNM Hitech Solutions - Join Our Innovative Team</title>
        <meta
          name="description"
          content="Explore career opportunities at VNM Hitech Solutions. We're looking for MERN Stack Developers, UI/UX Designers, and Mobile Developers. Shape the future with us."
        />
        <meta
          name="keywords"
          content="VNM Hitech Solutions careers, IT jobs in Karur, MERN stack developer jobs, software engineer openings, UI UX designer jobs Karur, mobile developer jobs"
        />
      </Helmet>
      <Header />

      <main>
        {/* Hero */}
        <section className="careers-hero section-container">
          <div className="careers-hero-content">
            <span className="tag">Join Our Team</span>
            <h1>Shape the Future with VNM Hitech Solutions</h1>
            <p>
              We believe in talent, passion, and innovation. Explore our open roles
              and find your next career adventure with us.
            </p>
          </div>
        </section>

        {/* Opening Positions */}
        <section className="opening-positions section-container">
          <h2>Opening Positions</h2>
          <p className="subtitle-text">
            Find the opportunity that matches your ambition.
          </p>
          <div className="jobs-grid">{openPositions.map(renderJobCard)}</div>
        </section>

        {/* Application Form */}
        <section
          id="apply-for-job"
          className="apply-section section-container"
          ref={applicationFormRef}
        >
          <h2>Apply for a Job</h2>
          <p className="subtitle-text">
            Upload your resume to Google Drive/Dropbox and paste the shareable link
            below.
          </p>

          <div className="apply-content-wrapper">
            {/* Animated Image */}
            <div
              className={`image-column ${imageAnimated ? "animate-in" : ""}`}
              ref={imageColumnRef}
            >
              <div className="image-card-career">
                <img
                  src="../images/developer.png"
                  alt="Developer working on laptop"
                  className="career-image"
                />
              </div>
            </div>

            {/* Form */}
            <div className="form-column card">
              <form onSubmit={handleSubmit} className="job-application-form">
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="Mobile Number"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="text"
                    name="location"
                    placeholder="Location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row form-row-select">
                  <select
                    name="position"
                    value={selectedPosition}
                    onChange={handleChange}
                    required
                  >
                    {jobTitles.map((title, index) => (
                      <option key={index} value={title} disabled={index === 0}>
                        {title}
                      </option>
                    ))}
                  </select>

                  <input
                    type="url"
                    name="resumeLink"
                    placeholder="Resume Link (Google Drive/Dropbox)"
                    value={formData.resumeLink}
                    onChange={handleChange}
                    required
                  />
                </div>

                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>

                <button type="submit" className="button">
                  Apply Now
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Success Modal */}
      {isSubmitted && (
        <div className="submission-modal-overlay">
          <div className="submission-modal card">
            <CheckCircle size={60} color="#28a745" className="check-icon" />
            <h3 className="submission-title">Profile Submitted Successfully!</h3>
            <p className="submission-message">
              Thank you! We will get back to you soon.
            </p>
            <button onClick={closeSuccessModal} className="button close-button">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
console.log("API:", process.env.REACT_APP_API_BASE_URL);

export default Careers;
